"use client";

import { useEffect, useRef } from "react";

interface HexCell {
  cx: number;
  cy: number;
  brightness: number;
}

export function GoldenEraBg() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const cellsRef = useRef<HexCell[]>([]);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const HEX_SIZE = 40;
    const HEX_H = HEX_SIZE * Math.sqrt(3);
    const MOUSE_RADIUS = 220;
    const RIPPLE_SPEED = 2.5;
    const DECAY = 0.97;

    const ripples: { x: number; y: number; radius: number; strength: number }[] = [];
    let lastRippleTime = 0;

    function buildGrid(w: number, h: number) {
      const cells: HexCell[] = [];
      const cols = Math.ceil(w / (HEX_SIZE * 1.5)) + 4;
      const rows = Math.ceil(h / (HEX_H * 0.5)) + 4;
      for (let row = -2; row < rows; row++) {
        for (let col = -2; col < cols; col++) {
          const offset = (row & 1) !== 0 ? HEX_SIZE * 0.75 : 0;
          const cx = col * HEX_SIZE * 1.5 + offset;
          const cy = row * HEX_H * 0.5;
          cells.push({ cx, cy, brightness: 0 });
        }
      }
      return cells;
    }

    function drawHex(cx: number, cy: number, size: number) {
      ctx!.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i - Math.PI / 6;
        const x = cx + size * Math.cos(angle);
        const y = cy + size * Math.sin(angle);
        if (i === 0) ctx!.moveTo(x, y);
        else ctx!.lineTo(x, y);
      }
      ctx!.closePath();
    }

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cellsRef.current = buildGrid(window.innerWidth, window.innerHeight);
    };

    resize();
    window.addEventListener("resize", resize);

    const onMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };

      const now = Date.now();
      if (now - lastRippleTime > 100) {
        ripples.push({ x: e.clientX, y: e.clientY, radius: 0, strength: 1 });
        lastRippleTime = now;
        if (ripples.length > 20) ripples.shift();
      }
    };
    window.addEventListener("mousemove", onMouseMove);

    let frame = 0;

    const draw = () => {
      frame++;
      const w = window.innerWidth;
      const h = window.innerHeight;
      ctx.clearRect(0, 0, w, h);

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      // Expand ripples
      for (let i = ripples.length - 1; i >= 0; i--) {
        ripples[i].radius += RIPPLE_SPEED;
        ripples[i].strength *= 0.985;
        if (ripples[i].strength < 0.01) {
          ripples.splice(i, 1);
        }
      }

      // Update and draw cells
      for (const cell of cellsRef.current) {
        // Ambient subtle pulse so grid is faintly visible everywhere
        const ambient =
          (Math.sin(frame * 0.008 + cell.cx * 0.01 + cell.cy * 0.012) * 0.5 + 0.5) * 0.04;

        // Mouse proximity brightness
        const dx = mx - cell.cx;
        const dy = my - cell.cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        let target = ambient;

        if (dist < MOUSE_RADIUS) {
          target = Math.max(target, (1 - dist / MOUSE_RADIUS) * 0.7);
        }

        // Ripple brightness
        for (const r of ripples) {
          const rdx = r.x - cell.cx;
          const rdy = r.y - cell.cy;
          const rDist = Math.sqrt(rdx * rdx + rdy * rdy);
          const ringDist = Math.abs(rDist - r.radius);
          if (ringDist < 30) {
            target = Math.max(target, (1 - ringDist / 30) * 0.5 * r.strength);
          }
        }

        // Smooth towards target
        cell.brightness += (target - cell.brightness) * 0.15;
        cell.brightness *= DECAY;

        if (cell.brightness < 0.003) continue;

        const alpha = cell.brightness;

        // Fill
        drawHex(cell.cx, cell.cy, HEX_SIZE * 0.48);
        ctx.fillStyle = `rgba(212, 160, 23, ${alpha * 0.08})`;
        ctx.fill();

        // Stroke
        drawHex(cell.cx, cell.cy, HEX_SIZE * 0.48);
        ctx.strokeStyle = `rgba(212, 160, 23, ${alpha * 0.4})`;
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      aria-hidden="true"
    />
  );
}
